import {useLayoutEffect, useRef, useState} from 'react';
import {Image as ImageIcon, MapPin} from 'lucide-react';

import {OUTPUT_SIZE, stampGeometry} from '@/lib/photo-capture/stamp-geometry';

import {slotHeightPx} from '../lib/element-size';
import {ReportImage} from './ReportImage';

/** The overlay slice of a photo slot's config — what gets burned, and which logo. */
export interface StampOverlayConfig {
  /** The slot's authored height, which picks the burned glyph — see
   *  `slotHeightPx`. Not a flag, but it decides what this preview draws. */
  height?: number;
  show_logo?: boolean;
  show_compass?: boolean;
  show_datetime?: boolean;
  show_latlng?: boolean;
  show_map?: boolean;
  logo_url?: string;
}

/**
 * Smallest each overlay is worth drawing at, in screen px.
 *
 * A **rendering** judgement, which is why it lives here and not in
 * `stampGeometry`: that file mirrors the server integer for integer, and a
 * floor inside it hid a real answer once already — clamping the label's width
 * to 16px turned the server's "no room, drop the map" into "one character of
 * text, keep the map", the exact collision the budget exists to prevent.
 *
 * Below these an overlay is a smudge, and a smudge reads as "no overlay" when
 * one is very much going to be burned in — so the preview draws it at the floor
 * and is honestly a little large, rather than vanishing and being wrong. The
 * photo box clips (`overflow-hidden`), so too large is contained.
 *
 * **Every drawn quantity needs an entry.** Moving the floors out of the geometry
 * and then flooring only three of five is how a 40x300 slot came to draw a 9px
 * metadata block around an 11px glyph: the geometry is right there — the server
 * shrinks its own font to fit, in a wrap loop this preview does not replicate —
 * so the honest preview is a legible block that overflows and clips, not an
 * illegible one that fits.
 */
const MIN_DRAWN = {
  margin: 2,
  /** The map thumbnail and logo box, which the server keeps at 3:2. */
  badgeWidth: 12,
  badgeHeight: 8,
  disc: 12,
  glyph: 7,
  /** Wide enough for the coordinate line to read as a line of text. */
  text: 40,
} as const;

/** Preview-only, with no server counterpart: it draws a needle polygon rather
 *  than a letter, and pastes a real map raster rather than a pin icon. */
const COMPASS_LETTER = 0.34;
const MAP_PIN = 0.4;

/**
 * A preview of the overlay the server burns into the stored photo.
 *
 * Its own component because it is the *only* thing in the photo slot that has to
 * agree with another repo, and it has to agree at every size. Living inside
 * `PhotoRenderer` it was a resize observer, a piece of state and a hundred lines
 * of positioned JSX tangled up with capture wiring and box sizing, in a file
 * that had crossed 1000 lines.
 *
 * The split of responsibility with `stampGeometry` is the point: that answers
 * *what the server will burn*, in the output's own pixels, and this decides
 * *what is worth drawing* at the size this box happens to be.
 *
 * **A preview, and only a preview.** Nothing here is interactive: the logo badge
 * used to be a button that opened the library, which made a click on a few
 * pixels of an image input mean "open a modal" while a click anywhere else on
 * the same element meant "select it". Choosing lives in the toolbar now — see
 * `PhotoOverlayControls`.
 */
export function PhotoStampOverlay({cfg}: {cfg: StampOverlayConfig}) {
  /**
   * The box's rendered size, watched.
   *
   * Measured rather than read off the config because the config does not know:
   * an image input with no width stretches to its column, and one authored
   * before the square lock existed records no shape at all. What is on screen is
   * the only place the answer lives.
   *
   * **Measures itself.** `inset-0` makes this element's own content box the box
   * being previewed, so it reads what it draws on rather than being handed a
   * size to trust. That holds while the parent is the bordered photo box and
   * carries no padding — pad that box and this silently previews the wrong
   * geometry, because `contentRect` would then be the padding box.
   *
   * Seeded square so the first frame draws sensible badges rather than nothing,
   * then corrected as soon as the observer reports.
   */
  const ref = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState({width: OUTPUT_SIZE, height: OUTPUT_SIZE});
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new ResizeObserver(([entry]) => {
      const r = entry?.contentRect;
      if (r && r.width > 0 && r.height > 0) setSize({width: r.width, height: r.height});
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // Both flags reach the geometry because the budget is a three-way
  // interaction: the label's width depends on whether a map is drawn, and
  // whether a map is drawn depends on whether the budget survives a label.
  const showMetadata = Boolean(cfg.show_datetime || cfg.show_latlng);
  const g = stampGeometry(size.width, size.height, {
    map: cfg.show_map ?? false,
    label: showMetadata,
    // The same height the upload sends, through the same reader — a preview
    // drawn from a different one would promise a glyph the burn does not make.
    slotHeight: slotHeightPx(cfg),
  });
  /** An output pixel, in screen pixels. */
  const px = (value: number) => value * g.displayScale;

  const margin = Math.max(MIN_DRAWN.margin, px(g.margin));
  const badge = {
    width: Math.max(MIN_DRAWN.badgeWidth, px(g.badge.width)),
    height: Math.max(MIN_DRAWN.badgeHeight, px(g.badge.height)),
  };
  const disc = Math.max(MIN_DRAWN.disc, px(g.compass));
  const glyph = Math.max(MIN_DRAWN.glyph, px(g.fontSize));
  const textWidth = Math.max(MIN_DRAWN.text, px(g.textWidth));

  return (
    <div ref={ref} className="pointer-events-none absolute inset-0">
      {/* Top-left: company logo */}
      {cfg.show_logo && (
        <div
          className="absolute flex items-center gap-1 rounded bg-white/85 px-1.5 py-0.5 shadow-sm"
          style={{top: margin, left: margin}}
        >
          {cfg.logo_url ? (
            <ReportImage
              src={cfg.logo_url}
              alt="Logo"
              className="object-contain"
              style={{maxWidth: badge.width, maxHeight: badge.height}}
            />
          ) : (
            <>
              <ImageIcon
                className="text-gray-500"
                style={{width: glyph, height: glyph}}
                strokeWidth={2}
              />
              <span className="font-semibold tracking-wide text-gray-600" style={{fontSize: glyph}}>
                LOGO
              </span>
            </>
          )}
        </div>
      )}

      {/* Top-right: compass */}
      {cfg.show_compass && (
        <div
          role="img"
          // The glyph is a bare "N", which reads as the letter N. What it means
          // is "a compass will be burned here".
          aria-label="Compass overlay"
          className="absolute flex items-center justify-center rounded-full bg-black/55 font-bold text-white"
          style={{
            top: margin,
            right: margin,
            width: disc,
            height: disc,
            fontSize: Math.max(MIN_DRAWN.glyph, Math.round(disc * COMPASS_LETTER)),
          }}
        >
          N
        </div>
      )}

      {/* Bottom-left: metadata (datetime + GPS) */}
      {showMetadata && (
        <div
          className="absolute rounded bg-black/55 px-1.5 py-0.5"
          style={{bottom: margin, left: margin, width: textWidth}}
        >
          {cfg.show_datetime && (
            <p className="font-mono leading-tight text-white" style={{fontSize: glyph}}>
              {new Date().toLocaleString()}
            </p>
          )}
          {cfg.show_latlng && (
            <p className="font-mono leading-tight text-white" style={{fontSize: glyph}}>
              3.1390° N, 101.6869° E
            </p>
          )}
        </div>
      )}

      {/* Bottom-right: map snapshot. `drawMap`, not `show_map` — the label's
          width budget may have withdrawn it, and the server withdraws it in the
          same place for the same reason. */}
      {g.drawMap && (
        <div
          role="img"
          aria-label="Location map overlay"
          className="absolute flex items-center justify-center overflow-hidden rounded border border-white/70 bg-gradient-to-br from-emerald-100 to-sky-100 shadow-sm"
          style={{bottom: margin, right: margin, width: badge.width, height: badge.height}}
        >
          <MapPin
            className="text-rose-500"
            style={{width: badge.height * MAP_PIN, height: badge.height * MAP_PIN}}
            strokeWidth={2.25}
          />
        </div>
      )}
    </div>
  );
}
